import { LOCAL_STORAGE_PERSIST_KEY } from 'src/consts/storage';
import type { Preference } from 'src/consts';
import type { ExtParams } from 'src/models';

type StoredData = {
  serverToken: string;
  preferences: Preference;
  extParams: ExtParams;
};

const DEFAULT_STORAGE: StoredData = {
  serverToken: '',
  preferences: { car_search_group_scope: null },
  extParams: {},
};

// ネストオブジェクト（preferences / extParams）を DEFAULT と明示的にマージする。
// トップレベルのシャローマージだけだと、将来フィールドが増えた際に旧ストレージの
// 欠損フィールドが DEFAULT から補完されないため。
const mergeWithDefault = (parsed: Partial<StoredData>): StoredData => ({
  serverToken: parsed.serverToken ?? DEFAULT_STORAGE.serverToken,
  preferences: { ...DEFAULT_STORAGE.preferences, ...parsed.preferences },
  extParams: { ...DEFAULT_STORAGE.extParams, ...parsed.extParams },
});

export const readStorage = (): StoredData => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_PERSIST_KEY);
    if (!raw) return mergeWithDefault({});
    // localStorage は外部データ境界のため、型が保証されない。as で境界変換する。
    return mergeWithDefault(JSON.parse(raw) as Partial<StoredData>);
  } catch {
    return mergeWithDefault({});
  }
};

// ネストオブジェクト（preferences / extParams）はトップレベルで置換する。
// 呼び出し側が常に完全なオブジェクトを渡す前提のため、ディープマージすると
// setExtParams({}) による明示的なクリアが無効化される。欠損フィールドの
// デフォルト補完は読み取り時の mergeWithDefault が担う。
export const writeStorage = (patch: Partial<StoredData>): void => {
  try {
    const current = readStorage();
    const merged: StoredData = {
      serverToken: patch.serverToken ?? current.serverToken,
      preferences: patch.preferences ?? current.preferences,
      extParams: patch.extParams ?? current.extParams,
    };
    localStorage.setItem(LOCAL_STORAGE_PERSIST_KEY, JSON.stringify(merged));
  } catch (error) {}
};

// StoredData に含めるほどではない個別機能向けの永続化領域を、
// 専用キーで読み書きするための汎用アクセサ。localStorage への直接アクセスは
// このファイルに集約し、各機能側からは必ずこちらを経由させる。
export const readStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaultValue;
    // localStorage は外部データ境界のため、型が保証されない。as で境界変換する。
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
};

export const writeStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {}
};
